import React, { useState } from "react";
import {
    FolderIcon, PlusIcon, MagnifyingGlassIcon, TrashIcon, XMarkIcon
} from "@heroicons/react/24/outline";
import {
    useAllCategories,
    useCreateCategory,
    useDeleteCategory
} from "../../Hooks/useCategories";
import { canAccess } from "../../utils/permissions";

export default function SubCategory() {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedParent, setSelectedParent] = useState("all");
    const [isAdding, setIsAdding] = useState(false);
    const [newName, setNewName] = useState("");
    const [newParentId, setNewParentId] = useState(null);

    const { data: allCategories = [], isLoading } = useAllCategories();
    const createMutation = useCreateCategory();
    const deleteMutation = useDeleteCategory();

    const mainCategories = allCategories.filter(c => c.parent_id === null);
    const subcategories = allCategories.filter(c => c.parent_id !== null);

    const filtered = subcategories.filter(s =>
        (selectedParent === "all" || s.parent_id === Number(selectedParent)) &&
        s.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const canCreate = canAccess('create_category');
    const canDelete = canAccess('delete_category');

    if (!canAccess("view_categories")) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center text-sm font-medium">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                    <FolderIcon className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
                <p className="text-gray-500 max-w-md font-medium">
                    You don't have the required permissions to view or manage categories.
                </p>
            </div>
        );
    }

    const handleAdd = () => {
        if (!canCreate) return;
        if (!newName.trim() || !newParentId) return;
        createMutation.mutate({ name: newName, parent_id: newParentId });
        setIsAdding(false);
        setNewName("");
    };

    return (
        <section className="space-y-8 py-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Subcategories</h1>
                    <p className="text-gray-600 mt-1">Manage detailed inspection items</p>
                </div>
                <div className="flex gap-3">
                    <select
                        value={selectedParent}
                        onChange={(e) => setSelectedParent(e.target.value)}
                        className="px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#1665f6] outline-none"
                    >
                        <option value="all">All Categories</option>
                        {mainCategories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search subcategories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-3 w-full sm:w-64 border rounded-xl focus:ring-2 focus:ring-[#1665f6] outline-none"
                        />
                    </div>
                    {canCreate && (
                        <button
                            onClick={() => setIsAdding(true)}
                            className="px-5 py-3 bg-gradient-to-r from-[#1665f6] to-[#8830b8] text-white rounded-xl font-bold flex items-center gap-2 shadow-lg hover:shadow-xl"
                        >
                            <PlusIcon className="w-5 h-5" />
                            Add Subcategory
                        </button>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                <div className="bg-gradient-to-r from-[#1665f6] to-[#0d51d4] rounded-2xl p-6 text-white shadow-lg">
                    <FolderIcon className="w-10 h-10 mb-3 opacity-90" />
                    <p className="text-sm opacity-90">Total Subcategories</p>
                    <p className="text-3xl font-bold mt-2">{subcategories.length}</p>
                </div>
                <div className="bg-gradient-to-r from-[#8830b8] to-purple-700 rounded-2xl p-6 text-white shadow-lg">
                    <p className="text-sm opacity-90">Parent Categories</p>
                    <p className="text-3xl font-bold mt-2">{mainCategories.length}</p>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
                    <p className="text-sm opacity-90">Active</p>
                    <p className="text-3xl font-bold mt-2">{subcategories.length}</p>
                </div>
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="text-center py-20 text-gray-500">Loading...</div>
            ) : (
                <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gradient-to-r from-[#1665f6] to-[#8830b8] text-white">
                                <tr>
                                    <th className="px-6 py-4 text-left">Subcategory</th>
                                    <th className="px-6 py-4 text-left">Parent Category</th>
                                    <th className="px-6 py-4 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filtered.map(sub => (
                                    <tr key={sub.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-5 font-medium text-gray-900">{sub.name}</td>
                                        <td className="px-6 py-5 text-gray-700">{sub.parent?.name || "—"}</td>
                                        <td className="px-6 py-5 text-center">
                                            <div className="flex justify-center gap-3">
                                                {canDelete && (
                                                    <button
                                                        onClick={() => deleteMutation.mutate(sub.id)}
                                                        className="p-2 bg-red-100 hover:bg-red-200 rounded-lg"
                                                    >
                                                        <TrashIcon className="w-4 h-4 text-red-600" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Add Subcategory Modal */}
            {isAdding && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold">Add Subcategory</h2>
                            <button onClick={() => setIsAdding(false)}>
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="space-y-5">
                            <input
                                type="text"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder="Subcategory name"
                                className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#1665f6] outline-none"
                            />
                            <select
                                onChange={(e) => setNewParentId(Number(e.target.value))}
                                className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#1665f6] outline-none"
                            >
                                <option value="">Choose parent category</option>
                                {mainCategories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setIsAdding(false)} className="px-5 py-3 border rounded-xl font-bold hover:bg-gray-50">
                                Cancel
                            </button>
                            <button
                                onClick={handleAdd}
                                disabled={createMutation.isPending}
                                className="px-5 py-3 bg-gradient-to-r from-[#1665f6] to-[#8830b8] text-white rounded-xl font-bold shadow-lg hover:shadow-xl disabled:opacity-70"
                            >
                                {createMutation.isPending ? "Adding..." : "Add Subcategory"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}